import {
  keepPreviousData,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  AddonInstallation,
  ContentRow,
  EpisodeSegments,
  Genre,
  HistoryEntry,
  HomeFeed,
  LibraryItem,
  MediaSummary,
  MovieDetails,
  PagedMedia,
  Preferences,
  Profile,
  ProgressLookup,
  ProviderInfo,
  ProviderStatus,
  SearchResults,
  SeasonDetails,
  SeriesDetails,
  SessionInfo,
  StreamListResponse,
  UpdatePreferencesInput,
  WatchlistEntry,
  MediaType,
  PersonSummary,
  ProfileAccess,
} from '@teaplus/shared';
import { api, isApiError } from './api';

export const qk = {
  session: ['session'] as const,
  platformStatus: ['platform-status'] as const,
  profiles: ['profiles'] as const,
  active: ['profile', 'active'] as const,
  home: (p: string) => ['home', p] as const,
  browse: (p: string, t: MediaType) => ['browse', p, t] as const,
  movie: (p: string, id: string) => ['movie', p, id] as const,
  series: (p: string, id: string) => ['series', p, id] as const,
  season: (p: string, id: string, s: number) => ['season', p, id, s] as const,
  segments: (id: string, s: number, e: number) => ['segments', id, s, e] as const,
  progress: (p: string, id: string) => ['progress', p, id] as const,
  continue: (p: string) => ['continue', p] as const,
  recent: (p: string) => ['recent', p] as const,
  history: (p: string) => ['history', p] as const,
  watchlist: (p: string) => ['watchlist', p] as const,
  watchlistIds: (p: string) => ['watchlist-ids', p] as const,
  library: (p: string) => ['library', p] as const,
  watchedIds: (p: string) => ['watched-ids', p] as const,
  addons: (p: string) => ['addons', p] as const,
  streams: (p: string, key: string) => ['streams', p, key] as const,
  provider: ['provider'] as const,
};

/**
 * Empties the cache when someone signs out or changes profile. The platform status stays in it (its
 * screens are always on the page and would lose touch with a removed query) and is checked again
 * for whoever is signed in now, so a restricted account or profile shows its screen straight away.
 */
export function clearCache(qc: QueryClient, keep: (key: unknown) => boolean = () => false) {
  qc.removeQueries({
    predicate: (q) => q.queryKey[0] !== qk.platformStatus[0] && !keep(q.queryKey[0]),
  });
  void qc.invalidateQueries({ queryKey: qk.platformStatus });
}

export function useSession() {
  return useQuery({
    queryKey: qk.session,
    queryFn: async () => {
      try {
        return await api<SessionInfo>('/api/auth/session');
      } catch (e) {
        if (isApiError(e, 'UNAUTHENTICATED')) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: 1,
  });
}

export function useProfiles(enabled = true) {
  return useQuery({
    queryKey: qk.profiles,
    queryFn: () =>
      api<{ profiles: Profile[]; limit: number; access?: ProfileAccess }>('/api/profiles'),
    enabled,
  });
}

export function useActiveProfile() {
  const session = useSession();
  return useQuery({
    queryKey: [...qk.active, session.data?.activeProfileId],
    queryFn: () => api<{ profile: Profile; preferences: Preferences }>('/api/profiles/active'),
    enabled: !!session.data?.activeProfileId,
    staleTime: 5 * 60_000,
  });
}

export function useProfileId() {
  return useSession().data?.activeProfileId ?? '';
}

export function usePreferences() {
  return useActiveProfile().data?.preferences;
}

type ActiveData = { profile: Profile; preferences: Preferences };

export function useUpdatePreferences() {
  const qc = useQueryClient();
  const write = (fn: (p: Preferences) => Preferences) =>
    qc.setQueriesData<ActiveData>({ queryKey: qk.active }, (old) =>
      old ? { ...old, preferences: fn(old.preferences) } : old,
    );
  return useMutation({
    mutationKey: ['preferences'],
    scope: { id: 'preferences' },
    mutationFn: (patch: UpdatePreferencesInput) =>
      api<Preferences>('/api/preferences', { method: 'PATCH', body: patch }),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: qk.active });
      const before = qc.getQueriesData<ActiveData>({ queryKey: qk.active });
      write((p) => ({ ...p, ...(patch as Partial<Preferences>) }));
      return { before };
    },
    onError: (_e, _patch, context) => {
      for (const [key, data] of context?.before ?? []) qc.setQueryData(key, data);
    },
    onSuccess: (prefs) => {
      if (qc.isMutating({ mutationKey: ['preferences'] }) <= 1) write(() => prefs);
    },
  });
}

export function useHome() {
  const p = useProfileId();
  return useQuery({
    queryKey: qk.home(p),
    queryFn: () => api<HomeFeed>('/api/content/home'),
    enabled: !!p,
    staleTime: 5 * 60_000,
  });
}

export function useBrowse(type: MediaType) {
  const p = useProfileId();
  return useQuery({
    queryKey: qk.browse(p, type),
    queryFn: () =>
      api<{ featured: MediaSummary[]; rows: ContentRow[]; source: ProviderInfo }>(
        `/api/content/browse/${type}`,
      ),
    enabled: !!p,
    staleTime: 5 * 60_000,
  });
}

export function useMovie(id: string) {
  const p = useProfileId();
  return useQuery({
    queryKey: qk.movie(p, id),
    queryFn: () => api<MovieDetails>(`/api/content/movies/${id}`),
    enabled: !!p && !!id,
    staleTime: 10 * 60_000,
    retry: (n, e) => !isApiError(e, 'MATURITY_RESTRICTED') && !isApiError(e, 'NOT_FOUND') && n < 2,
  });
}

export function useSeries(id: string) {
  const p = useProfileId();
  return useQuery({
    queryKey: qk.series(p, id),
    queryFn: () => api<SeriesDetails>(`/api/content/series/${id}`),
    enabled: !!p && !!id,
    staleTime: 10 * 60_000,
    retry: (n, e) => !isApiError(e, 'MATURITY_RESTRICTED') && !isApiError(e, 'NOT_FOUND') && n < 2,
  });
}

export function useSeason(id: string, season: number | null) {
  const p = useProfileId();
  return useQuery({
    queryKey: qk.season(p, id, season ?? -1),
    queryFn: () => api<SeasonDetails>(`/api/content/series/${id}/seasons/${season}`),
    enabled: !!p && !!id && season !== null,
    staleTime: 10 * 60_000,
    placeholderData: keepPreviousData,
  });
}

/** Skip Intro, Skip Recap and Skip Credits for an episode; none when unknown or the lookup fails. */
export function useEpisodeSegments(id: string, season?: number, episode?: number) {
  const known = !!id && season !== undefined && episode !== undefined;
  return useQuery({
    queryKey: qk.segments(id, season ?? -1, episode ?? -1),
    queryFn: () =>
      api<EpisodeSegments>(
        `/api/content/series/${id}/seasons/${season}/episodes/${episode}/segments`,
      ).catch(() => ({ segments: [] }) as EpisodeSegments),
    enabled: known,
    staleTime: 60 * 60_000,
    retry: false,
  });
}

export function useGenres(type: MediaType) {
  const p = useProfileId();
  return useQuery({
    queryKey: ['genres', type],
    queryFn: () => api<Genre[]>(`/api/content/genres/${type}`),
    enabled: !!p,
    staleTime: Infinity,
  });
}

export function useDiscover(params: Record<string, string | number | undefined>) {
  const p = useProfileId();
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '') as [string, string][],
  );
  return useQuery({
    queryKey: ['discover', p, qs.toString()],
    queryFn: () => api<PagedMedia>(`/api/content/discover?${qs}`),
    enabled: !!p,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
  });
}

export function useSearch(q: string, type: string, page: number, record: boolean) {
  const p = useProfileId();
  return useQuery({
    queryKey: ['search', p, q, type, page],
    queryFn: ({ signal }) =>
      api<SearchResults>(
        `/api/content/search?${new URLSearchParams({ q, type, page: String(page), record: String(record) })}`,
        { signal },
      ),
    enabled: !!p && q.trim().length > 0,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
  });
}

export function useSuggest(q: string) {
  const p = useProfileId();
  return useQuery({
    queryKey: ['suggest', p, q],
    queryFn: ({ signal }) =>
      api<{ titles: MediaSummary[]; people: PersonSummary[] }>(
        `/api/content/search/suggest?q=${encodeURIComponent(q)}`,
        { signal },
      ),
    enabled: !!p && q.trim().length > 1,
    staleTime: 5 * 60_000,
  });
}

export function useContinueWatching() {
  const p = useProfileId();
  return useQuery({
    queryKey: qk.continue(p),
    queryFn: () => api<HistoryEntry[]>('/api/history/continue'),
    enabled: !!p,
  });
}

export function useRecentlyWatched() {
  const p = useProfileId();
  return useQuery({
    queryKey: qk.recent(p),
    queryFn: () => api<HistoryEntry[]>('/api/history/recent'),
    enabled: !!p,
  });
}

export function useProgress(mediaId: string) {
  const p = useProfileId();
  return useQuery({
    queryKey: qk.progress(p, mediaId),
    queryFn: () => api<ProgressLookup>(`/api/history/progress/${mediaId}`),
    enabled: !!p && !!mediaId,
  });
}

export function useWatchlist(type = 'all', sort = 'added') {
  const p = useProfileId();
  return useQuery({
    queryKey: [...qk.watchlist(p), type, sort],
    queryFn: () => api<WatchlistEntry[]>(`/api/watchlist?type=${type}&sort=${sort}`),
    enabled: !!p,
  });
}

export function useWatchlistIds() {
  const p = useProfileId();
  return useQuery({
    queryKey: qk.watchlistIds(p),
    queryFn: async () => new Set(await api<string[]>('/api/watchlist/ids')),
    enabled: !!p,
    staleTime: 60_000,
  });
}

export function useLibrary() {
  const p = useProfileId();
  return useQuery({
    queryKey: qk.library(p),
    queryFn: () => api<LibraryItem[]>('/api/library'),
    enabled: !!p,
  });
}

export function useWatchedIds() {
  const p = useProfileId();
  return useQuery({
    queryKey: qk.watchedIds(p),
    queryFn: async () => new Set(await api<string[]>('/api/library/watched-ids')),
    enabled: !!p,
    staleTime: 60_000,
  });
}

export function useInvalidateLibrary() {
  const qc = useQueryClient();
  const p = useProfileId();
  return () => {
    for (const key of [
      qk.continue(p),
      qk.recent(p),
      qk.history(p),
      qk.watchlist(p),
      qk.watchlistIds(p),
      qk.library(p),
      qk.watchedIds(p),
      qk.home(p),
    ]) {
      qc.invalidateQueries({ queryKey: key });
    }
    qc.invalidateQueries({ queryKey: ['progress', p] });
  };
}

export function useAddons() {
  const p = useProfileId();
  return useQuery({
    queryKey: qk.addons(p),
    queryFn: () =>
      api<{ addons: AddonInstallation[]; devFixtureUrl: string | null }>('/api/addons'),
    enabled: !!p,
  });
}

export function useProviderStatus(enabled = true) {
  return useQuery({
    queryKey: qk.provider,
    queryFn: () => api<ProviderStatus>('/api/providers/status'),
    enabled,
    staleTime: 60_000,
  });
}

export function useStreams(
  params: { type: MediaType; id: string; season?: number; episode?: number } | null,
) {
  const p = useProfileId();
  const key = params
    ? `${params.type}:${params.id}:${params.season ?? ''}:${params.episode ?? ''}`
    : '';
  return useQuery({
    queryKey: qk.streams(p, key),
    queryFn: () => {
      const qs = new URLSearchParams({ type: params!.type, id: params!.id });
      if (params!.season !== undefined) qs.set('season', String(params!.season));
      if (params!.episode !== undefined) qs.set('episode', String(params!.episode));
      return api<StreamListResponse>(`/api/streams?${qs}`);
    },
    enabled: !!p && !!params,
    staleTime: 10 * 60_000,
    retry: false,
  });
}
